<template>
	<div class="common-tab" :class="['common-tab--' + type]">
		<div v-show="showLeft" class="common-tab-item prev-btn" @click="scrollPrev">
			<SvgIcons :name="nextBtnIcon" />
		</div>
		<div class="tab-scroll" ref="tabScroll">
			<div class="tab-wrapper">
				<div
					v-for="(item, index) in option"
					:key="index"
					class="common-tab-item"
					:class="{ 'common-tab-item--active': item.value == innerValue }"
					@click="changeTab(item, index)"
					ref="tabRefs"
				>
					{{ item.label }}
				</div>
			</div>
		</div>
		<div v-show="showRight" class="common-tab-item next-btn" @click="scrollNext">
			<SvgIcons :name="prevBtnIcon" />
		</div>
	</div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue';

const props = defineProps({
	value: {
		// eslint-disable-next-line vue/require-prop-type-constructor
		type: String | Number,
		default: ''
	},
	type: {
		type: String,
		default: 'normal' // 默认 normal | 矩形 rect | 矩形 rect-primary | 内联 inner | ...
	},
	option: {
		type: Array,
		default: () => [] // { label, value }
	}
});
const emits = defineEmits(['change', 'update:value']);
const innerValue = computed({
	get: () => props.value,
	set: val => {
		emits('update:value', val);
	}
});
const changeTab = (item, idx) => {
	if (innerValue.value == item.value) return;
	innerValue.value = item.value;
	emits('change', {
		index: idx,
		value: item.value,
		data: props.option[idx]
	});
};

const tabScroll = ref(null);
const tabRefs = ref([]);
const showLeft = ref(false);
const showRight = ref(false);
let currentTab = 0;
watch(
	() => props.option,
	() => {
		currentTab = 0;
		nextTick(() => {
			const { offsetWidth = 0, scrollWidth = 0 } = tabScroll.value;
			if (scrollWidth > offsetWidth) {
				showRight.value = true;
			}
		});
	},
	{
		immediate: true
	}
);
function scrollPrev() {
	showRight.value = true;
	nextTick(() => {
		// 父元素范围
		const { left: parentLeft } = tabScroll.value.getBoundingClientRect();
		const { offsetLeft: parentOffset, offsetWidth: parentWidth } = tabScroll.value;
		for (let i = currentTab; i >= 0; i--) {
			const { left: childLeft } = tabRefs.value[i].getBoundingClientRect();
			// 当前子元素超出显示范围
			if (childLeft < parentLeft && parentLeft - childLeft > 2) {
				currentTab = i;
				const { offsetLeft, offsetWidth } = tabRefs.value[i];
				const itemOffset = Math.max(0, offsetLeft + offsetWidth - parentOffset - parentWidth);
				tabScroll.value.scrollLeft = itemOffset;
				// 滚动到最左边
				if (itemOffset === 0) {
					showLeft.value = false;
				}
				return;
			}
		}
	});
}
function scrollNext() {
	showLeft.value = true;
	nextTick(() => {
		// 父元素范围
		const { right: parentRight } = tabScroll.value.getBoundingClientRect();
		const { offsetLeft: parentOffset } = tabScroll.value;
		for (let i = currentTab; i < tabRefs.value.length; i++) {
			const { right: childRight } = tabRefs.value[i].getBoundingClientRect();
			// 当前子元素超出显示范围
			if (parentRight < childRight && childRight - parentRight > 2) {
				currentTab = i;
				const { offsetLeft } = tabRefs.value[i];
				const itemOffset = offsetLeft - parentOffset;
				tabScroll.value.scrollLeft = itemOffset;
				// 滚动到最右边
				if (itemOffset + tabScroll.value.offsetWidth > tabScroll.value.scrollWidth) {
					showRight.value = false;
				}
				return;
			}
		}
	});
}

const nextBtnIcon = computed(() => {
	return props.type === 'surf' ? 'left-arrow' : 'left';
});
const prevBtnIcon = computed(() => {
	return props.type === 'surf' ? 'left-arrow' : 'right-light';
});
</script>

<style lang="less" scoped>
.common-tab {
	max-width: 100%;
	display: inline-flex;
	align-items: center;

	.tab-scroll {
		flex: 1;
		min-width: 0;
		overflow-x: auto;
		scroll-behavior: smooth;

		&::-webkit-scrollbar {
			display: none;
		}
	}
	.tab-wrapper {
		display: inline-flex;
	}
	.prev-btn,
	.next-btn {
		.svg-icon {
			color: var(--secondary-color) !important;
		}
	}

	&-item {
		flex-shrink: 0;
		padding: 0 12px;
		font-family: Barlow;

		background: transparent;
		cursor: pointer;

		&--active {
			font-weight: 500;
		}
	}

	// 区分不同类型的样式
	&--normal {
		.common-tab-item {
			padding: 0 4px;
			height: 55px;
			font-size: 24px;
			color: #86909c;
			position: relative;

			& + .common-tab-item {
				margin-left: 60px;
			}
			&--active {
				color: #1d2129;

				&::after {
					content: '';
					position: absolute;
					left: 8px;
					right: 8px;
					bottom: 0;
					height: 4px;
					background: var(--theme-color);
				}
			}
		}
		.prev-btn {
			margin-right: 60px;
		}
		.next-btn {
			margin-left: 60px;
		}
	}
	&--rect {
		.common-tab-item {
			height: 24px;
			line-height: 22px;
			color: #4e5969;
			border: 1px solid #e5e6eb;
			font-size: 15px;

			& + .common-tab-item {
				margin-left: 8px;
			}
			&--active {
				color: #fff;
				background: var(--theme-color);
				border: 1px solid var(--theme-color);
			}
		}
		.prev-btn {
			margin-right: 8px;
		}
		.next-btn {
			margin-left: 8px;
		}
	}
	&--rect-primary {
		.common-tab-item {
			padding: 0 16px;
			height: 32px;
			border-radius: 2px;
			background: var(--fill-normal-color);

			color: var(--secondary-info-color);

			font-size: 14px;
			font-weight: 400;
			line-height: 32px;

			& + .common-tab-item {
				margin-left: 16px;
			}

			&--active {
				color: var(--theme-color);
				font-weight: 500;
				background: linear-gradient(0deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.8) 100%),
					var(--theme-color);
			}
		}
		.prev-btn {
			margin-right: 16px;
		}
		.next-btn {
			margin-left: 16px;
		}
		.prev-btn,
		.next-btn {
			width: 32px;
			padding: 0;
			text-align: center;
		}
	}
	&--inner {
		padding: 4px;
		border-radius: 2px;
		background: #f2f3f5;

		.common-tab-item {
			height: 32px;
			line-height: 32px;
			color: #86909c;
			font-size: 16px;

			&--active {
				color: var(--theme-color);
				background: #fff;
			}
		}
	}

	&--surf {
		.common-tab-item {
			color: rgba(255, 255, 255, 0.4);
			font-size: 20px;
			font-weight: 400;
			line-height: 28px; /* 140% */
			&--active {
				color: #fff;
				font-size: 24px;
				font-weight: 600;
				line-height: 32px; /* 133.333% */
			}
		}
		.prev-btn {
			margin-right: 12px;
		}
		.next-btn {
			margin-left: 12px;
			transform: rotate(180deg);
		}
		.prev-btn, .next-btn {
			border:1px solid var(--surf-inverse-color);
			border-radius: 50%;
			width: 32px;
			height: 32px;
			display: flex;
			align-items: center;
			justify-content: center;
		    .svg-icon {
				width: 16px;
				height: 16px;
				flex-shrink: 0;
				color: var(--surf-inverse-color)!important;
			}
		}
	}
}
</style>
